import React, { useEffect, useState } from 'react';
import { getIpInfo } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { useNavigate, Link, useSearchParams, type NavigateFunction } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setProjectDetails } from '../redux/slices/projectSlice';
import { clearMenuCache } from '../Common/menuCacheUtils';
import { getDashboardRoute, getDashboardRouteState } from '../auth/getDashboardRoute';
import { isAdminUser } from '../auth/roleUtils';
import { validateInvitation } from '../services/invitationApi';
import { AuthLayout } from '../components/layout/AuthLayout';
import { Loading } from '../components/ui/Spinner';
import { AuthTextField } from '../components/auth/AuthTextField';
import { AuthPasswordField } from '../components/auth/AuthPasswordField';
import { AuthInviteBadges } from '../components/auth/AuthInviteBadges';
import { AuthPolicyBlock } from '../components/auth/AuthPolicyBlock';
import { AuthSubmitButton } from '../components/auth/AuthSubmitButton';
import { AuthCardFooter } from '../components/auth/AuthCardFooter';

const navigateAfterAuth = (
  navigate: NavigateFunction,
  authUser: { roleName?: string; userType?: string; state?: string; userId: number },
  basename: string,
  email: string
) => {
  const route = getDashboardRoute(authUser, basename, { email });
  const routeState = getDashboardRouteState(authUser, { email });
  if (routeState) {
    navigate(route, { state: routeState, replace: true });
  } else {
    navigate(route, { replace: true });
  }
};

function getInviteErrorMessage(errorCode?: string) {
  switch (errorCode) {
    case 'Expired':
      return 'This invitation has expired.';
    case 'Consumed':
      return 'This invitation has already been used.';
    case 'Revoked':
      return 'This invitation is no longer valid.';
    default:
      return 'This invitation link is not valid.';
  }
}

const SignIn = () => {
  const basename = process.env.PUBLIC_URL || '';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);

  const [inviteToken, setInviteToken] = useState('');
  const [projectName, setProjectName] = useState('');
  const [inviteRoleName, setInviteRoleName] = useState('');
  const [isEmailLocked, setIsEmailLocked] = useState(false);
  const [isValidatingInvite, setIsValidatingInvite] = useState(false);
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    const token = searchParams.get('invite');
    if (!token) {
      setInviteToken('');
      setIsValidatingInvite(false);
      setInviteError('');
      return;
    }

    setInviteToken(token);
    setIsValidatingInvite(true);
    setInviteError('');

    validateInvitation(token)
      .then((result) => {
        if (!result.isValid) {
          setInviteError(getInviteErrorMessage(result.errorCode));
          return;
        }

        if (!result.accountExists && result.isEmailLocked) {
          navigate(`/signup?invite=${encodeURIComponent(token)}`, { replace: true });
          return;
        }

        setProjectName(result.projectDisplayName || '');
        setInviteRoleName(result.roleDisplayName || '');
        setIsEmailLocked(Boolean(result.isEmailLocked));
        if (result.isEmailLocked && result.emailAddress) {
          setEmail(result.emailAddress);
        }
      })
      .catch(() => {
        setInviteError('Unable to validate this invitation. Please try again later.');
      })
      .finally(() => {
        setIsValidatingInvite(false);
      });
  }, [searchParams, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setServerError('');

    try {
      clearMenuCache();

      const { ipAddress, userAgent } = await getIpInfo();
      const user = await login(
        email,
        password,
        ipAddress,
        userAgent,
        inviteToken || undefined
      );

      if (isAdminUser(user)) {
        localStorage.removeItem('projectId');
      } else {
        const projId = user.projectDetails?.[0]?.projectId ?? 1;
        localStorage.setItem('projectId', String(projId));
      }

      dispatch(setProjectDetails(user.projectDetails));
      navigateAfterAuth(navigate, user, basename, email);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong. Please try again later.';
      setServerError(message);
    } finally {
      setLoading(false);
    }
  };

  const signUpHref = inviteToken
    ? `/signup?invite=${encodeURIComponent(inviteToken)}`
    : '/signup';

  if (isValidatingInvite) {
    return (
      <AuthLayout>
        <Loading className="py-16" text="Validating invitation…" />
      </AuthLayout>
    );
  }

  if (inviteError) {
    return (
      <AuthLayout title="Invitation unavailable" subtitle="This link cannot be used to log in.">
        <div className="auth-alert auth-alert--error" role="alert">
          {inviteError}
        </div>
        <Link to="/signin" className="auth-link">
          Return to login
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Login"
      subtitle={
        inviteToken
          ? 'Log in with your existing password to join this project.'
          : 'Enter your account details.'
      }
    >
      <AuthInviteBadges
        projectName={projectName}
        roleName={inviteRoleName}
        lockedEmail={isEmailLocked && email ? email : undefined}
      />

      {serverError ? (
        <div className="auth-alert auth-alert--error" role="alert">
          {serverError}
        </div>
      ) : null}

      <form className="auth-form" onSubmit={handleSubmit}>
        <AuthTextField
          id="email"
          label="Email"
          type="email"
          value={email}
          onChange={(event) => {
            if (!isEmailLocked) setEmail(event.target.value);
          }}
          placeholder="johndoe@example.com"
          autoComplete="email"
          readOnly={isEmailLocked}
          required
          hint={
            isEmailLocked
              ? 'Email is fixed for this invitation and cannot be changed.'
              : undefined
          }
        />

        <AuthPasswordField
          id="password"
          label="Password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="Enter your password"
          autoComplete="current-password"
          required
        />

        <p className="auth-forgot">
          <Link to="/forgotpassword" className="auth-link">
            Forgot password?
          </Link>
        </p>

        <AuthSubmitButton loading={loading}>
          {loading
            ? inviteToken
              ? 'Joining project…'
              : 'Logging in…'
            : inviteToken
              ? 'Login to join project'
              : 'Login'}
        </AuthSubmitButton>
      </form>

      <AuthCardFooter>
        <p className="auth-footer-text">
          Not registered?{' '}
          <Link to={signUpHref} className="auth-link">
            Sign up
          </Link>
        </p>
        <AuthPolicyBlock />
      </AuthCardFooter>
    </AuthLayout>
  );
};

export default SignIn;
